<template>
  <section>
    <zg-border-module
      v-if="backgroundSettings && (backgroundSettings.borderTop || backgroundSettings.border)"
      :border="backgroundSettings.border"
      :border-color="backgroundSettings.backgroundColor"
      :background-color="backgroundSettings.borderColor"
    />

    <div
      :id="contentId"
      :class="[
        'zg-module',
        `zg-module--${backgroundSettings.backgroundColor}`,
        `zg-module--${padding}-padding`,
      ]"
    >
      <div class="zg-module__container">
        <div
          :class="['zg-module__container__head', {
            [`zg-module__container__head--${align}`]: align,
            'zg-module__container__head--full-width': fullWidth || (headerTitle && link && link.text) || headerLink
          }]"
        >
          <zg-flex
            class="zg-module__title"
            justify-content="space-between"
            :l-columns="$slots['title'] ? 2 : 1"
            :m-columns="$slots['title'] ? 2 : 1"
            gap="small"
            wrap
          >
            <h2
              v-if="title"
              :class="{ 'zg-module__grid-no-margin': $slots['title'] }"
              v-html="title"
            />

            <slot name="title" />
          </zg-flex>

          <div
            v-if="$slots['sub-title']"
            class="zg-module__sub-title"
          >
            <slot name="sub-title" />
          </div>

          <div
            v-if="headerTitle || headerLink"
            class="zg-module__show-more-container"
          >
            <span
              v-if="headerTitle"
              class="zg-module__show-more-title"
            >
              {{ headerTitle }}
            </span>

            <zg-link-button
              v-if="headerLink && headerLink.href && headerLink.text"
              :link="headerLink.href"
              :icons="{ right: 'arrow-forward' }"
            >
              {{ headerLink.text }}
            </zg-link-button>

            <zg-link-button
              v-else-if="link && link.href && link.text"
              :link="link.href"
              :icons="{ right: 'arrow-forward' }"
            >
              {{ link.text }}
            </zg-link-button>
          </div>
        </div>

        <slot />

        <zg-flex
          v-if="link && link.href && link.text && !headerTitle"
          justify-content="center"
          class="cta"
        >
          <zg-button-link
            :href="link.href"
            :text="link.text"
            :button-props="{
              size: 'large',
              icons: { right: 'arrow-forward' },
              fullWidthMobile: true
            }"
          />
        </zg-flex>
      </div>
    </div>

    <zg-border-module
      v-if="backgroundSettings && (backgroundSettings.borderBottom || backgroundSettings.border)"
      :border="backgroundSettings.border"
      :border-color="backgroundSettings.backgroundColor"
      :background-color="backgroundSettings.borderColor"
      flipped-x
      flipped-y
    />
  </section>
</template>

<script>
  import ZgFlex from '../../layout/flex/ZgFlex.vue'
  import ZgButtonLink from '../../ZgButtonLink/ZgButtonLink'
  import ZgLinkButton from '../../ZgLinkButton/ZgLinkButton.vue'
  import ZgBorderModule from './ZgBorderModule.vue'

  export default {
    name: 'ZgModule',
    components: { ZgButtonLink, ZgBorderModule, ZgFlex, ZgLinkButton },
    props: {
      /**
       * The id of the section
       */
      contentId: {
        type: String,
        default: null
      },

      /**
       * The title of the module
       */
      title: {
        type: String,
        default: null
      },
      /**
       * headerTitle of the section
       */
      headerTitle: {
        type: String,
        default: null
      },
      /**
       * Show link in combination with headerTitle of the section
       */
      link: {
        type: Object,
        default: null
      },
      /**
       * headerLink of the section
       */
      headerLink: {
        type: Object,
        default: null
      },
      /**
       * The background settings of the module
       * background color|border top|border bottom
       */
      backgroundSettings: {
        type: Object,
        default: () => ({
          backgroundColor: 'white',
          borderTop: false,
          borderBottom: false
        }),
        validator: ({ backgroundColor }) => ['white', 'beige', 'transparent', 'brand-100'].includes(backgroundColor)
      },
      /**
       * The vertical padding of the module
       * xsmall|small|base|medium|large
       */
      padding: {
        type: String,
        default: 'base',
        validator: (value) =>
          ['xsmall', 'small', 'base', 'medium', 'large'].includes(value)
      },
      /**
       * Text align of the content
       */
      align: {
        type: String,
        default: 'left',
        validator: (value) => ['left', 'center', 'right'].includes(value)
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      }
    }
  }
</script>

<style lang="scss" scoped>
  $background-colors: white, beige, brand-100;
  $aligns: left, center, right;

  .zg-module {
    width: 100%;
    padding: rhythm() rhythm(small);
    position: relative;

    @each $name in $background-colors {
      &--#{"" + $name} {
        background-color: color($name);
      }
    }

    @each $rhythm, $lines in $rhythms {
      &--#{$rhythm}-padding {
        padding-top: rhythm($rhythm);
        padding-bottom: rhythm($rhythm);
      }
    }

    &__grid-no-margin {
      margin: 0;
    }

    &__container {
      max-width: rem(1080);
      margin-left: auto;
      margin-right: auto;

      &__head {
        max-width: rem(720);

        &--center {
          margin: 0 auto;
        }

        &--full-width {
          max-width: unset;
        }

        @each $align in $aligns {
          &--#{"" + $align} {
            text-align: $align;
          }
        }
      }
    }

    &__show-more-container {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: space-between;
      gap: rhythm(small);
      margin-bottom: rhythm(base);

      @include mq(medium) {
        flex-direction: row;
        gap: rhythm(base);
      }
    }

    &__show-more-title {
      @include type-title-l;

      margin: 0;
    }

    &__title {
      align-items: baseline;

      h2 {
        margin: 0 0 rem(16px);
      }
    }

    &__sub-title {

      p,
      ::v-deep p {
        margin: 0 0 rem(32px);
      }
    }
  }

  .cta {
    margin: rhythm(medium) 0 0;
  }
</style>
